<template>
  <div v-if="!isKioskModeEnabled" qaid="add-to-cart-container" class="mt-2">
    <pdl-callout v-if="isStockStatusVisible" qaid="stock-status-message" :kind="stockStatusCalloutType">
      <template #content>
        <span>{{ stockStatusText }}</span>
      </template>
    </pdl-callout>
    <fulfillment-options-container v-if="variantCode && !isCustomizationMode" :variant-code="variantCode" />
    <div v-if="isStoreFinderOptionShowing" class="mb-1">
      <div v-if="isRetailerOnly" class="alert negative">{{ $t('product.instore.only') }}</div>
      <pdl-link
        button
        primary
        block
        icon="pin_drop"
        :href="withContextPath('/store-finder/')"
        data-ga-event="find-a-store-cta"
        data-ga-category="Find a Store CTA"
        data-ga-action="Item Not Available for Purchase"
      >
        <span>{{ $t('product.find.retailer.button') }}</span>
      </pdl-link>
    </div>
    <div v-else-if="isAddToCartVisible" class="mb-1">
      <pdl-button
        v-if="!isCustomizationMode"
        id="addToCartButton"
        vibrant
        block
        icon="shopping_cart"
        :disabled="!isAddToCartEnabled"
        type="submit"
        @click.prevent="addToCart"
      >
        <span id="add-button" v-html="addToCartButtonCaption" />
      </pdl-button>
      <div v-else class="px-2 lg:px-0">
        <customization-submit-to-retailer-button />
      </div>
      <add-to-cart-status :message="message" />
    </div>

    <pdp-replacement v-if="product.isArchived && variantCode" :replacement-product="replacementProduct" />

    <slot name="locally-button"></slot>

    <slot name="customize-button"></slot>

    <!-- CMS consumer message slot -->
    <slot name="consumer-messages"></slot>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import trackProductAddToCart from '@/mixins/tracking/product/track-product-add-to-cart.js';
import AddToCartStatus from '@/components/containers/buy-zone/AddToCartStatus.vue';
import FulfillmentOptionsContainer from '@/components/containers/buy-zone/b2c/fulfillment-options/FulfillmentOptionsContainer.vue';
import {Url} from '@/utils/url';
import get from 'lodash/get';
import PdpReplacement from '@/components/containers/pdp/PdpReplacement.vue';
import {isBtoCode, isInStockByCode} from '@/components/containers/pdp/utils/retailer-helpers.js';
import {PdlButton} from '@pedal/pdl-button';
import {PdlLink} from '@pedal/pdl-link';
import Variant from '@/utils/variant-helpers.js';
import {PdlCallout} from '@pedal/pdl-callout';
import CustomizationSubmitToRetailerButton from '@/components/containers/buy-zone/customization/CustomizationSubmitToRetailerButton.vue';

export default {
  name: 'AddToCartContainer',
  components: {
    PdpReplacement,
    AddToCartStatus,
    PdlButton,
    PdlLink,
    FulfillmentOptionsContainer,
    PdlCallout,
    CustomizationSubmitToRetailerButton,
  },
  mixins: [trackProductAddToCart],
  props: {
    addToCartButtonCaption: {
      type: String,
      default: '',
    },
    variantCode: {
      type: String,
      default: '',
    },
    isAddingToCart: {
      type: Boolean,
      default: false,
    },
    stockMessage: {
      type: Object,
      default: () => null,
    },
    product: {
      type: Object,
      required: true,
    },
    isPurchasable: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    trackEventData: {
      type: Object,
      default: () => ({}),
    },
    isPdp: {
      type: Boolean,
      default: false,
    },
    isLocallyStockAvailable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quantity: 1,
    };
  },
  computed: {
    ...mapState('productDetails', ['isECommAvailable', 'isLocallyActive', 'isCustomizationMode']),
    ...mapState('backend', ['b2b']),

    isStockStatusVisible() {
      return (
        (isBtoCode(this.currentVariant?.globalStockStatus) || !this.currentVariant) &&
        this.stockMessage &&
        this.stockStatusText &&
        !this.isRetailerOnly &&
        !this.isCartRestrictionVisible &&
        !this.isCustomizationMode
      );
    },

    stockStatusText() {
      if (this.b2b || this.isECommAvailable) {
        return this.$t(`product.stockStatus.${this.stockMessage.description}`);
      } else {
        if (this.isLocallyActive) {
          return this.$t(`product.stockStatus.${this.stockMessage.nonECommDescription}`);
        } else {
          return this.$t(`product.stockStatus.${this.stockMessage.nonECommNonLocallyDescription}`);
        }
      }
    },

    stockStatusCalloutType() {
      if (this.isECommAvailable) {
        return this.stockMessage.calloutType;
      } else {
        return 'default';
      }
    },

    isKioskModeEnabled() {
      return this.options.kioskModeEnabledOnSite;
    },

    currentVariant() {
      return Variant(this.variantCode, this.product).get();
    },

    isAddToCartVisible() {
      return !this.product.isArchived;
    },

    isAddToCartEnabled() {
      let isWareHouseStockAvailable = this.isPurchasable && isInStockByCode(this.currentVariant?.globalStockStatus);
      return !this.isAddingToCart && (this.isLocallyStockAvailable || isWareHouseStockAvailable);
    },

    isCartRestrictionVisible() {
      return isInStockByCode(this.currentVariant?.globalStockStatus);
    },

    replacementProduct() {
      if (!this.variantCode) {
        return null;
      }

      return get(
        this.product.variants.find((variant) => variant.code === this.variantCode),
        'replacementProduct'
      );
    },

    isRetailerOnly() {
      return this.product.retailerOnly;
    },

    isStoreFinderOptionShowing() {
      return (this.isRetailerOnly || !this.options.eCommAvailable) && !this.isCustomizationMode;
    },
  },

  methods: {
    withContextPath: (url) => Url.withContextPath(url),
    addToCart() {
      this.$emit('add-to-cart', this.quantity);
      this.trackProductAddToCart();

      window._fbq('track', 'AddToCart', {
        contents: [this.product.code],
        content_ids: [this.variantCode],
        content_type: 'product',
      });
    },
  },
};
</script>
